import React from 'react';
import {Box, TextField, Typography} from "@mui/material";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';

const MeasurementCard = ({type, label, unit, value, previousValue, previousMeasurementDate, onChange}) => {

    // Unit is provided by the company definition; fall back to the legacy type-based guess.
    const getUnit = () => {
        if (unit) return unit;
        if (type === 'WEIGHT') {
            return 'ק"ג';
        }
        return 'ס"מ';
    };

    // Format the date for display - now using previousMeasurementDate
    const getFormattedDate = () => {
        if (!previousMeasurementDate) {
            return 'אין תאריך קודם';
        }

        // Handle if previousMeasurementDate comes as an array [year, month, day]
        let dateObj;
        if (Array.isArray(previousMeasurementDate)) {
            const [year, month, day] = previousMeasurementDate;
            dateObj = new Date(year, month - 1, day); // month is 0-indexed in JS
        } else if (typeof previousMeasurementDate === 'string') {
            // Split the yyyy-MM-dd manually instead of `new Date(str)`. The spec parses a bare
            // date-only string as UTC MIDNIGHT, and the local getters below then read it in the
            // viewer's zone — so west of UTC "2026-07-01" rendered as 30/06/2026. The array
            // branch above already builds a local date; this makes the two agree.
            const [year, month, day] = previousMeasurementDate.slice(0, 10).split('-').map(Number);
            if (!year || !month || !day) return 'תאריך לא תקין';
            dateObj = new Date(year, month - 1, day);
        } else {
            return 'תאריך לא תקין';
        }

        // Deterministic DD/MM/YYYY (Israeli convention), matching the display cards' "20/05" style
        // and avoiding the awkward "20 2026 במאי" word order of the localized month name.
        const dd = String(dateObj.getDate()).padStart(2, '0');
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        return `${dd}/${mm}/${dateObj.getFullYear()}`;
    };

    // Calculate trend based on current and previous values
    const getTrendIcon = () => {
        if (!previousValue || !value || previousValue === '' || value === '') {
            return <RemoveIcon sx={{ color: "#94A3B8", fontSize: "18px", mr: "8px"}} />;
        }

        const diff = parseFloat(value) - parseFloat(previousValue);
        const formattedDiff = diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1);

        if (diff > 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: "8px" }}>
                    <TrendingUpIcon sx={{ color: "#EF4444", fontSize: "18px"}} />
                    <Typography sx={{ color: "#EF4444", fontSize: "14px", fontWeight: 500, ml: 0.5 }}>
                        {formattedDiff}
                    </Typography>
                </Box>
            );
        } else if (diff < 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: "8px" }}>
                    <TrendingDownIcon sx={{ color: "#14B8A6", fontSize: "18px"}} />
                    <Typography sx={{ color: "#14B8A6", fontSize: "14px", fontWeight: 500, ml: 0.5 }}>
                        {formattedDiff}
                    </Typography>
                </Box>
            );
        } else {
            return <RemoveIcon sx={{ color: "#94A3B8", fontSize: "18px", mr: "8px"}} />;
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                padding: "20px",
            }}
        >
            {/* Header with date and trend icon.
                Both sides need explicit flex rules: a long measurement name ("שכיבות סמיכה באחיזה
                רחבה בהגבהה אחורית") otherwise runs straight through the date/trend on its left,
                because `space-between` alone gives neither side a shrink or wrap constraint. */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1,
                mb: 2
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,           // the date/trend keeps its width...
                    whiteSpace: "nowrap"
                }}>

                    <Typography sx={{
                        fontSize: "14px",
                        color: "#94A3B8",
                        fontWeight: 400
                    }}>
                        {getFormattedDate()}
                    </Typography>
                    {getTrendIcon()}
                </Box>
                {/* Measurement Title */}
                <Typography sx={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#1E293B",
                    textAlign: "left",
                    minWidth: 0,             // ...and the title wraps instead of overflowing
                    overflowWrap: "anywhere",
                    lineHeight: 1.3
                }}>
                    {label} <span style={{ color: "#94A3B8", fontWeight: 400 }}>({getUnit()})</span>
                </Typography>

            </Box>

            {/* Input Fields.
                `flex: 1` alone lets the two inputs squeeze below a usable width on a narrow card
                (the placeholder and value then clip); the basis + minWidth keeps them readable and
                wraps to two rows instead. */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2}}>
                {/* New Value Input */}
                <Box sx={{ flex: "1 1 120px", minWidth: "120px"}}>
                    <Typography sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        mb: 0.5,
                        textAlign: "left"
                    }}>
                        ערך חדש
                    </Typography>
                    <TextField
                        fullWidth
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="הזן ערך"
                        type="number"
                        inputProps={{
                            step: "0.1",
                            min: "0"
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: "42px",
                                backgroundColor: "#FFFFFF",
                                borderRadius: "8px",
                                '& fieldset': {
                                    borderColor: '#E2E8F0'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#94A3B8'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#5B9A8B'
                                }
                            },
                            '& input': {
                                textAlign: 'left',
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#1E293B'
                            },
                            '& input::placeholder': {
                                fontSize: '14px',
                                color: '#CBD5E1',
                                opacity: 1
                            }
                        }}
                    />
                </Box>

                {/* Previous Value */}
                <Box sx={{ flex: "1 1 120px", minWidth: "120px" }}>
                    <Typography sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        mb: 0.5,
                        textAlign: "left"
                    }}>
                        ערך אחרון
                    </Typography>
                    <TextField
                        fullWidth
                        value={previousValue || ''}
                        placeholder="אין"
                        disabled
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: "#F8FAFC",
                                height: "42px",
                                borderRadius: "8px",
                                '& fieldset': {
                                    borderColor: '#E2E8F0'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: "#F8FAFC",
                                    '& fieldset': {
                                        borderColor: '#E2E8F0'
                                    }
                                }
                            },
                            '& input': {
                                textAlign: 'left',
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#64748B'
                            },
                            '& input::placeholder': {
                                fontSize: '14px',
                                color: '#CBD5E1',
                                opacity: 1
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: '#94A3B8'
                            }
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default MeasurementCard;